import { ReactElement, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dropdown,
  Field,
  Label,
  Paragraph,
  Input,
  Skeleton,
  Table,
  Tag,
} from '@digdir/designsystemet-react';
import { MenuElipsisHorizontalIcon, PlusIcon } from '@navikt/aksel-icons';
import { OrganizationLayout } from '../../../components/OrganizationLayout';
import { useGetOrganizationUsersQuery } from '../../../hooks';
import { useRemoveUserFromOrganizationMutation, useActivateUserMutation } from '../../../hooks/mutations';
import { RolesUtils } from '../../../utils/RolesUtils';
import { OrganizationUser, UserRoles } from '../../../api/queries';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import { ConfirmDialog } from '../../SurveysPage/components/ConfirmDialog';
import { UserStatusTag } from './components/UserSummary';
import classes from './Users.module.css';

type PendingAction = { kind: 'remove' | 'activate'; user: OrganizationUser } | null;

/** Den første verdien som tag, og resten som «+N» med alle i tittelen */
const FirstAndMore = ({ items, color }: { items: string[]; color: 'neutral' | 'accent' }) => {
  if (items.length === 0) return <span className={classes.subtle}>–</span>;
  return (
    <span className={classes.tags}>
      <Tag data-size="sm" data-color={color}>
        {items[0]}
      </Tag>
      {items.length > 1 && (
        <Tag data-size="sm" data-color={color} title={items.join(', ')}>
          +{items.length - 1}
        </Tag>
      )}
    </span>
  );
};

export const UsersOverviewPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: users, isLoading, isError } = useGetOrganizationUsersQuery();
  const removeUserMutation = useRemoveUserFromOrganizationMutation();
  const activateUserMutation = useActivateUserMutation();
  const [query, setQuery] = useState('');
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);

  const orgAdminCount = users?.filter((user) => user.roles.includes(UserRoles.ORG_ADMIN)).length ?? 0;
  const canRemove = (user: OrganizationUser) => !user.roles.includes(UserRoles.ORG_ADMIN) || orgAdminCount > 1;
  const filtered = (users ?? []).filter((user) => user.email.toLowerCase().includes(query.trim().toLowerCase()));

  const closeDialog = () => setPending(null);

  const confirm = () => {
    if (!pending) return;
    const { kind, user } = pending;
    if (kind === 'activate') {
      activateUserMutation.mutate(user.id, {
        onSuccess: () => {
          toast.success(t('users.overview.toast.userActivated'));
          closeDialog();
        },
        onError: (error: any) =>
          toast.error(error?.response?.data?.message || t('users.overview.toast.activateError')),
      });
      return;
    }
    if (!canRemove(user)) {
      toast.error(t('users.overview.toast.cannotRemoveLastAdmin'));
      closeDialog();
      return;
    }
    removeUserMutation.mutate(user.id, {
      onSuccess: () => {
        toast.success(t('users.overview.toast.userRemoved', { email: user.email }));
        closeDialog();
      },
      onError: (error: any) => toast.error(error?.response?.data?.message || t('users.overview.toast.removeError')),
    });
  };

  const runFromMenu = (action: () => void) => () => {
    setOpenMenu(null);
    action();
  };

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true">
          <span className="ds-sr-only">{t('users.overview.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={240} />
        </div>
      );
    }
    if (isError) {
      return (
        <Alert data-color="danger">
          <Paragraph>{t('users.overview.error')}</Paragraph>
        </Alert>
      );
    }

    return (
      <>
        <div className={classes.toolbar}>
          <Field className={classes.search}>
            <Label htmlFor="user-search">{t('organizationPage.users.search')}</Label>
            <Input
              id="user-search"
              type="search"
              placeholder={t('organizationPage.users.searchPlaceholder')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </Field>
          <Button asChild>
            <Link to={Routes.OrganizationInviteUser}>
              <PlusIcon aria-hidden />
              {t('users.overview.addUserButton')}
            </Link>
          </Button>
        </div>

        <div className={classes.tableScroll}>
          <Table className={classes.table}>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>{t('organizationPage.users.columns.user')}</Table.HeaderCell>
                <Table.HeaderCell>{t('users.overview.table.headers.status')}</Table.HeaderCell>
                <Table.HeaderCell>{t('users.overview.table.headers.roles')}</Table.HeaderCell>
                <Table.HeaderCell>{t('users.overview.table.headers.teams')}</Table.HeaderCell>
                <Table.HeaderCell>
                  <span className="ds-sr-only">{t('organizationPage.users.columns.actions')}</span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {filtered.map((user) => {
                const removable = canRemove(user);
                return (
                  <Table.Row key={user.id}>
                    <Table.Cell>
                      <span className={classes.userCell}>
                        <span aria-hidden="true" className={classes.avatar}>
                          {user.email[0]?.toUpperCase()}
                        </span>
                        <span className={classes.userEmail}>{user.email}</span>
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <UserStatusTag user={user} />
                    </Table.Cell>
                    <Table.Cell>
                      <FirstAndMore items={RolesUtils.getRoleLabels(user.roles)} color="neutral" />
                    </Table.Cell>
                    <Table.Cell>
                      <FirstAndMore items={user.teamNames} color="accent" />
                    </Table.Cell>
                    <Table.Cell className={classes.actionsCell}>
                      <Dropdown.TriggerContext>
                        <Dropdown.Trigger
                          variant="tertiary"
                          data-size="sm"
                          icon
                          aria-label={t('organizationPage.users.actionsFor', { email: user.email })}
                        >
                          <MenuElipsisHorizontalIcon aria-hidden />
                        </Dropdown.Trigger>
                        <Dropdown
                          placement="bottom-end"
                          open={openMenu === user.id}
                          onOpen={() => setOpenMenu(user.id)}
                          onClose={() => setOpenMenu(null)}
                        >
                          <Dropdown.List>
                            <Dropdown.Item>
                              <Dropdown.Button
                                onClick={runFromMenu(() =>
                                  navigate(buildRoute(Routes.OrganizationViewUser, { userId: user.id })),
                                )}
                              >
                                {t('users.overview.actions.viewUser')}
                              </Dropdown.Button>
                            </Dropdown.Item>
                            {user.isPending && (
                              <Dropdown.Item>
                                <Dropdown.Button
                                  onClick={runFromMenu(() => setPending({ kind: 'activate', user }))}
                                >
                                  {t('users.overview.actions.activateUser')}
                                </Dropdown.Button>
                              </Dropdown.Item>
                            )}
                            <Dropdown.Item>
                              <Dropdown.Button
                                onClick={runFromMenu(() =>
                                  navigate(buildRoute(Routes.OrganizationEditUser, { userId: user.id })),
                                )}
                              >
                                {t('users.overview.actions.editRoles')}
                              </Dropdown.Button>
                            </Dropdown.Item>
                            <Dropdown.Item>
                              <Dropdown.Button
                                data-color="danger"
                                disabled={!removable}
                                title={removable ? undefined : t('users.overview.actions.cannotRemoveLastAdmin')}
                                onClick={runFromMenu(() => setPending({ kind: 'remove', user }))}
                              >
                                {t('users.overview.actions.removeUser')}
                              </Dropdown.Button>
                            </Dropdown.Item>
                          </Dropdown.List>
                        </Dropdown>
                      </Dropdown.TriggerContext>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
        <Paragraph data-size="sm" className={classes.subtle} role="status">
          {t('organizationPage.users.count', { count: filtered.length })}
        </Paragraph>
      </>
    );
  };

  return (
    <OrganizationLayout>
      {content()}

      {pending?.kind === 'remove' && (
        <ConfirmDialog
          open
          title={t('organizationPage.users.removeDialog.title', { email: pending.user.email })}
          text={t('users.overview.confirm.removeUser', { email: pending.user.email })}
          confirmLabel={t('users.overview.actions.removeUser')}
          dismissLabel={t('teamSettings.common.cancel')}
          isPending={removeUserMutation.isPending}
          onConfirm={confirm}
          onClose={closeDialog}
        />
      )}
      {pending?.kind === 'activate' && (
        <ConfirmDialog
          open
          title={t('users.overview.actions.activateUser')}
          text={t('users.overview.confirm.activateUser')}
          confirmLabel={t('users.overview.actions.activateUser')}
          dismissLabel={t('teamSettings.common.cancel')}
          isPending={activateUserMutation.isPending}
          onConfirm={confirm}
          onClose={closeDialog}
        />
      )}
    </OrganizationLayout>
  );
};
